import React, { useEffect, useState } from 'react';
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getGlobalRoleLabel } from '../utils/roles';
import {
  Users,
  LayoutDashboard,
  Settings,
  LogOut,
  Briefcase,
  Ticket,
  Send,
  ChevronRight,
  History
} from 'lucide-react';

const SERVICE_TOPBAR_KEY = 'service-topbar-breadcrumb';

const MainLayout: React.FC = () => {
  const { user, logout, isPendingApproval } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = user?.globalRole === 'ROOT_ADMIN';
  const brandLabel = isAdmin ? 'omongkr 관리자용' : 'omongkr 대시보드';
  const [serviceBreadcrumb, setServiceBreadcrumb] = useState<{ code: string; label: string } | null>(null);
  const topbarBreadcrumb = (() => {
    if (location.pathname.startsWith('/services')) {
      return (
        <div className="app-topbar-breadcrumb">
          <span>서비스 관리</span>
          {serviceBreadcrumb?.label && (
            <>
              <ChevronRight size={14} />
              <span>{serviceBreadcrumb.label}</span>
            </>
          )}
        </div>
      );
    }

    if (location.pathname.startsWith('/user-requests')) {
      return (
        <div className="app-topbar-breadcrumb">
          <span>유저 신청 관리</span>
        </div>
      );
    }

    if (location.pathname.startsWith('/service-applications')) {
      return (
        <div className="app-topbar-breadcrumb">
          <span>서비스 신청</span>
        </div>
      );
    }

    if (location.pathname.startsWith('/users')) {
      return (
        <div className="app-topbar-breadcrumb">
          <span>유저 관리</span>
        </div>
      );
    }

    if (location.pathname.startsWith('/ad-removal-management')) {
      return (
        <div className="app-topbar-breadcrumb">
          <span>광고제거 관리</span>
        </div>
      );
    }

    if (location.pathname.startsWith('/audit-logs')) {
      return (
        <div className="app-topbar-breadcrumb">
          <span>로그 관리</span>
        </div>
      );
    }

    if (location.pathname.startsWith('/ad-removal')) {
      return (
        <div className="app-topbar-breadcrumb">
          <span>광고 제거</span>
        </div>
      );
    }

    if (location.pathname.startsWith('/profile')) {
      return (
        <div className="app-topbar-breadcrumb">
          <span>설정</span>
        </div>
      );
    }

    if (location.pathname === '/') {
      return (
        <div className="app-topbar-breadcrumb">
          <span>{isAdmin ? '관리자 대시보드' : '내 서비스'}</span>
        </div>
      );
    }

    return null;
  })();

  useEffect(() => {
    const syncBreadcrumb = () => {
      if (location.pathname.startsWith('/services')) {
        try {
          const raw = localStorage.getItem(SERVICE_TOPBAR_KEY);
          setServiceBreadcrumb(raw ? JSON.parse(raw) : null);
        } catch {
          setServiceBreadcrumb(null);
        }
      } else {
        setServiceBreadcrumb(null);
      }
    };

    syncBreadcrumb();
    window.addEventListener('service-topbar-change', syncBreadcrumb);
    window.addEventListener('storage', syncBreadcrumb);
    return () => {
      window.removeEventListener('service-topbar-change', syncBreadcrumb);
      window.removeEventListener('storage', syncBreadcrumb);
    };
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = isPendingApproval
    ? [{ path: '/pending-approval', icon: <LayoutDashboard size={18} />, label: '승인 대기' }]
      : isAdmin
      ? [
          { path: '/', icon: <LayoutDashboard size={18} />, label: '관리자 대시보드' },
          { path: '/user-requests', icon: <Send size={18} />, label: '유저 신청 관리' },
          { path: '/users', icon: <Users size={18} />, label: '유저 관리' },
          { path: '/ad-removal-management', icon: <Ticket size={18} />, label: '광고제거 관리' },
          { path: '/services', icon: <Briefcase size={18} />, label: '서비스 관리', className: 'app-nav-link-service' },
          { path: '/audit-logs', icon: <History size={18} />, label: '로그 관리' },
        ]
      : [
          { path: '/', icon: <LayoutDashboard size={18} />, label: '내 서비스' },
          { path: '/service-applications', icon: <Send size={18} />, label: '서비스 신청' },
          { path: '/ad-removal', icon: <Ticket size={18} />, label: '광고 제거' },
        ];

  return (
    <div className="app-shell">
      <aside className="app-sidebar app-sidebar-top">
        <div className="app-brand">
          {brandLabel}
        </div>

        <nav className="app-nav">
          <div className="app-nav-label">Menu</div>
          {navItems.map((item) => {
            const isActive = location.pathname === item.path || (item.path !== '/' && location.pathname.startsWith(item.path));
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`app-nav-link ${item.className ?? ''} ${isActive ? 'active' : ''}`}
              >
                {item.icon}
                <span>{item.label}</span>
                {isActive && <ChevronRight size={14} className="app-nav-arrow" />}
              </Link>
            );
          })}
        </nav>
      </aside>

      <main className="app-main">
        <header className="app-topbar">
          <div className="app-topbar-left">
            {topbarBreadcrumb}
          </div>
          <Link to="/profile" className="app-profile-btn">
            <Settings size={18} />
            <span>설정</span>
          </Link>
        </header>

        <div className="container app-container fade-in">
          <Outlet />
        </div>
      </main>

      <aside className="app-sidebar-footer app-sidebar-footer-mobile">
        <div className="app-user">
          <div className="app-user-meta">
            <div className="app-user-name">{user?.name}</div>
            <div className="app-user-email">{user?.email}</div>
            <div className="app-user-email" style={{ fontSize: '0.72rem' }}>
              {getGlobalRoleLabel(user?.globalRole)}
            </div>
          </div>
        </div>
        <button onClick={handleLogout} className="app-logout-btn">
          <LogOut size={18} />
          <span>로그아웃</span>
        </button>
      </aside>
    </div>
  );
};

export default MainLayout;
